import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Badge, eur, dt, Btn } from "../components/common";
import { toast } from "sonner";
import {
  Repeat, FileText, LifeBuoy, Users, Eye, Search,
  Building2, ChevronRight, ArrowLeft,
} from "lucide-react";

const TABS = [
  { key: "subscricoes", label: "Subscricoes", icon: Repeat },
  { key: "faturas", label: "Faturas e recibos", icon: FileText },
  { key: "suporte", label: "Service Desk", icon: LifeBuoy },
  { key: "acessos", label: "Acessos portal", icon: Users },
];

export default function PortalPreview() {
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("subscricoes");

  useEffect(() => {
    api.get("/portal-admin/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, []);

  const selectAccount = async (acc) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/portal-admin/preview/${acc.id}`);
      setSelected(acc);
      setData(d);
      setTab("subscricoes");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const filtered = accounts.filter((a) => {
    const q = search.toLowerCase();
    return !q || (a.nome || "").toLowerCase().includes(q) || (a.nif || "").includes(q) || (a.email || "").toLowerCase().includes(q);
  });

  if (!selected) {
    return (
      <div>
        <PageHeader overline="Portal do Cliente" title="Selecionar conta (alter-ego)" />
        <p className="text-sm text-muted2 mb-4">
          Escolha uma conta de cliente para visualizar o portal tal como o cliente o veria.
        </p>
        <div className="relative mb-4 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pesquisar por nome, NIF ou email..."
            data-testid="portal-preview-search"
            className="w-full pl-8 pr-3 py-2 border border-tint rounded-lg text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue focus:outline-none"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="portal-preview-accounts">
          {filtered.length === 0 && <p className="text-muted2 text-sm col-span-3">Sem contas de cliente encontradas.</p>}
          {filtered.map((a) => (
            <button
              key={a.id}
              onClick={() => selectAccount(a)}
              disabled={loading}
              data-testid={`portal-preview-account-${a.id}`}
              className="text-left border border-tint rounded-xl p-4 bg-white hover:border-blue hover:shadow-md transition-all group disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-navy/10 flex items-center justify-center shrink-0">
                  <Building2 size={18} className="text-navy" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-navy text-sm truncate">{a.nome}</div>
                  <div className="text-xs text-muted2 truncate">{a.nif ? `NIF ${a.nif}` : ""}{a.email ? ` - ${a.email}` : ""}</div>
                </div>
                <ChevronRight size={16} className="text-muted2 group-hover:text-blue shrink-0" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const acc = data?.account || {};
  const subs = data?.subscriptions || [];
  const invoices = data?.invoices || [];
  const tickets = data?.tickets || [];
  const portalUsers = data?.portal_users || [];

  return (
    <div>
      <div className="mb-4">
        <button
          onClick={() => { setSelected(null); setData(null); }}
          data-testid="portal-preview-back"
          className="inline-flex items-center gap-1.5 text-sm text-muted2 hover:text-navy transition-colors"
        >
          <ArrowLeft size={14} /> Voltar a selecao
        </button>
      </div>

      <div className="rounded-xl border border-blue/20 bg-gradient-to-r from-navy to-navy/90 p-5 mb-6" data-testid="portal-preview-header">
        <div className="flex items-center gap-2 mb-1">
          <Eye size={16} className="text-blue" />
          <span className="text-blue text-[10px] uppercase tracking-[0.2em] font-semibold">Vista alter-ego</span>
        </div>
        <h1 className="text-white text-xl font-bold tracking-tight" data-testid="portal-preview-org">{acc.nome}</h1>
        <div className="text-white/60 text-xs mt-1">
          {acc.nif && `NIF ${acc.nif}`}{acc.email && ` - ${acc.email}`}
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-tint overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`portal-preview-tab-${t.key}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === t.key ? "border-blue text-navy" : "border-transparent text-muted2 hover:text-navy"
            }`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "subscricoes" && (
        <div className="space-y-3" data-testid="portal-preview-subscriptions">
          {subs.length === 0 && <p className="text-muted2 text-sm">Sem subscricoes.</p>}
          {subs.map((s) => (
            <div key={s.id} className="bg-white rounded-xl border border-tint p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold text-navy">{s.plano_nome || s.plataforma || "Subscricao"}</div>
                <div className="text-xs text-muted2 mt-1">
                  MRR {eur(s.mrr || 0)}
                  {s.periodo_atual_fim && ` - renova ${dt(s.periodo_atual_fim)}`}
                  {s.plataforma && <span className="ml-2 text-blue">{s.plataforma}</span>}
                </div>
                {s.pagamento_recorrente && <span className="text-[11px] text-green-600">Renovacao automatica ativa</span>}
              </div>
              <Badge value={s.estado} />
            </div>
          ))}
        </div>
      )}

      {tab === "faturas" && (
        <div className="space-y-3" data-testid="portal-preview-invoices">
          {invoices.length === 0 && <p className="text-muted2 text-sm">Sem faturas.</p>}
          {invoices.map((f) => (
            <div key={f.id} className="bg-white rounded-xl border border-tint p-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-navy">{f.moloni_document_id || "Fatura"} - {eur(f.total ?? f.valor_total)}</div>
                <Badge value={f.estado} />
              </div>
              {f.mb_entidade && f.mb_referencia && (
                <div className="text-xs text-muted2 mt-2 inline-flex gap-3 bg-tint rounded p-2">
                  <span>Ent. <b>{f.mb_entidade}</b></span>
                  <span>Ref. <b>{f.mb_referencia}</b></span>
                  {f.mb_valor != null && <span>{eur(f.mb_valor)}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "suporte" && (
        <div className="space-y-3" data-testid="portal-preview-tickets">
          {tickets.length === 0 && <p className="text-muted2 text-sm">Sem pedidos de suporte.</p>}
          {tickets.map((t) => (
            <div key={t.id} className="bg-white rounded-xl border border-tint p-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-navy">{t.assunto}</div>
                <div className="flex items-center gap-2">
                  <Badge value={t.prioridade} />
                  <Badge value={t.estado} />
                </div>
              </div>
              {(t.mensagens || []).length > 0 && (
                <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                  {(t.mensagens || []).map((m, idx) => (
                    <div key={idx} className={`text-sm p-2 rounded-lg ${m.tipo === "cliente" ? "bg-tint" : "bg-blue/10"}`}>
                      <span className="text-[11px] text-muted2">{m.autor} - {dt(m.em)}</span>
                      <div>{m.mensagem}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "acessos" && (
        <div className="space-y-3" data-testid="portal-preview-users">
          {portalUsers.length === 0 && <p className="text-muted2 text-sm">Nenhum utilizador do portal criado para esta conta.</p>}
          {portalUsers.map((u) => (
            <div key={u.id} className="bg-white rounded-xl border border-tint p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold text-navy">{u.nome}</div>
                <div className="text-xs text-muted2">{u.email}</div>
              </div>
              <Badge value={u.ativo !== false ? "ativo" : "inativo"} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
